import React, { useEffect } from 'react';
import { X } from "lucide-react";
import HighlightedQuery from 'HighlightedQuery';

const COLUMN_TOKENS = ['first', 'last', 'email', 'phone', 'national', 'military', 'linkedin'];

const EXAMPLES = [
  { query: 'last:cohen email:gmail', note: 'Matches both terms' },
  { query: 'email:gmail email:yahoo', note: 'Matches either address' },
  { query: 'first:dan -linkedin:sales', note: 'Excludes sales profiles' },
  { query: 'last:"ben gurion"', note: 'Quoted value with a space' },
  { query: 'has:linkedin -has:phone', note: 'Has LinkedIn, no phone' },
  { query: 'last:cohen -has:email', note: 'Last name Cohen, no email on file' },
];

// Help Modal Component
const HelpModal = ({ isOpen, onClose, isDark }) => {
    useEffect(() => {
      const handleEscape = (event) => {
        if (event.key === 'Escape') {
          onClose();
        }
      };

      if (isOpen) {
        document.addEventListener('keydown', handleEscape);
      }

      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const mutedText = isDark ? 'text-gray-500' : 'text-gray-400';
    const codeBg = isDark ? 'bg-night-header' : 'bg-brand-50';

    return (
      <div
        className="fixed inset-0 bg-night bg-opacity-70 flex items-center justify-center p-4 z-50"
        onClick={onClose}
      >
        <div
          className={`w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col rounded-lg
          ${isDark ? 'bg-night-raised' : 'bg-white'}`}
          onClick={e => e.stopPropagation()}
        >
          <div className={`p-4 border-b flex justify-between items-center flex-none
            ${isDark ? 'bg-night-header border-brand-900 text-brand-200' : 'bg-brand-50 border-brand-100 text-brand-900'}`}>
            <h2 className={`text-lg font-semibold
              ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Search help
            </h2>
            <button
              onClick={onClose}
              className={`${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className={`p-6 overflow-y-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            <div className="space-y-5">
              <section>
                <p className="font-semibold mb-2">Quick start</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Type at least 3 characters to search.</li>
                  <li>
                    Plain text searches First Name, Last Name, Email, and Phone Number by
                    default. To change these, select <strong>Search Columns</strong>.
                  </li>
                </ul>
              </section>

              <section>
                <p className="font-semibold mb-2">Search Syntax:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    <code>column:value</code> searches one column. Tokens:{' '}
                    {COLUMN_TOKENS.map((token, i) => (
                      <React.Fragment key={token}>
                        {i > 0 && ', '}
                        <code>{token}</code>
                      </React.Fragment>
                    ))}.
                  </li>
                  <li>A leading <code>-</code> excludes a match.</li>
                  <li>Quote a value that contains spaces, e.g. <code>"ben gurion"</code>.</li>
                  <li>
                    Different columns narrow the results; repeating a column widens it.
                  </li>
                  <li>
                    <code className={isDark ? 'text-emerald-300' : 'text-emerald-600'}>has:</code>
                    <code>column</code> matches a non-empty column; negate it (<code>-has:</code>)
                    to match an empty one (NULL or "").
                  </li>
                </ul>
              </section>

              <section>
                <p className="font-semibold mb-2">Examples</p>
                <div className={`font-mono text-sm rounded-lg p-3 space-y-1 ${codeBg}`}>
                  {EXAMPLES.map(({ query, note }) => (
                    <div key={query}>
                      <HighlightedQuery query={query} knownTokens={COLUMN_TOKENS} isDark={isDark} />
                      {' '}
                      <span className={mutedText}>— {note}</span>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
          <div className={`p-4 border-t flex-none ${isDark ? 'border-brand-900' : 'border-brand-100'}`}>
            <button
              onClick={onClose}
              className={`w-full px-4 py-2 rounded-lg
                ${isDark
                  ? 'bg-night-header hover:bg-brand-900 text-brand-100'
                  : 'bg-brand-100 hover:bg-brand-200 text-brand-900'}`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  export default HelpModal;
